import React, { useState, useEffect, useCallback } from "react";
import config from "../../Config";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import AccountsMenu from "./AccountsMenu";
import { NavLink, Link } from "react-router-dom";
import moment from "moment";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function IncomeExpenditure() {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));
  const [DefaultFYear] = useState(
    () => localStorage.getItem("DefaultFYear") || ""
  );

  const [AccountLevel, setAccountLevel] = useState("0");
  const [StartDate, setStartDate] = useState("");
  const [EndDate, setEndDate] = useState(() =>
    moment().format(config.date_format_input)
  );

  const [loading, setLoading] = useState(false);
  const [LedgerIncome, setLedgerIncome] = useState([]);
  const [LedgerExpenses, setLedgerExpenses] = useState([]);
  const [IncomeTotal, setIncomeTotal] = useState(0);
  const [ExpanditureTotal, setExpanditureTotal] = useState(0);
  const [SurplusOrDeficit, setSurplusOrDeficit] = useState(0);

  const [Account_Code, setAccount_Code] = useState("");
  const [Account_Description, setAccount_Description] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [appliedName, setAppliedName] = useState("");
  const [zeroAccounts, setZeroAccounts] = useState(true);

  const fyStartDate = DefaultFYear
    ? `${DefaultFYear}-04-01`
    : moment().format(config.date_format_input);

  const fetchIncomeExpenditure = useCallback(() => {
    const year = parseInt(DefaultFYear, 10);
    if (!year) {
      setLedgerIncome([]);
      setLedgerExpenses([]);
      toast.error("Financial year is not set. Use Accounts → Change Year.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (!EndDate) {
      return;
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Accounts/GetIncomeExpenditure",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        pageNo: 1,
        pageSize: 10000,
        FYear: year,
        StartDate: StartDate || fyStartDate,
        EndDate,
        AccountLevel: parseInt(AccountLevel, 10) || 0,
        ShowZeroAccounts: zeroAccounts,
        AccountCode: appliedCode,
        AccountName: appliedName,
      },
    })
      .then((response) => {
        if (response.data?.status_code === 1) {
          setLedgerIncome(
            Array.isArray(response.data?.IncomeAccounts)
              ? response.data.IncomeAccounts
              : []
          );
          setLedgerExpenses(
            Array.isArray(response.data?.ExpenditureAccounts)
              ? response.data.ExpenditureAccounts
              : []
          );
          setIncomeTotal(response.data?.IncomeTotal ?? 0);
          setExpanditureTotal(response.data?.ExpanditureTotal ?? 0);
          setSurplusOrDeficit(response.data?.SurplusOrDeficit ?? 0);
        } else {
          setLedgerIncome([]);
          setLedgerExpenses([]);
          setIncomeTotal(0);
          setExpanditureTotal(0);
          setSurplusOrDeficit(0);
          if (response.data?.status_message) {
            toast.info(response.data.status_message, {
              position: toast.POSITION.TOP_RIGHT,
            });
          }
        }
      })
      .catch(() => {
        setLedgerIncome([]);
        setLedgerExpenses([]);
        toast.error("Network Error..", {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => setLoading(false));
  }, [
    AccessKey,
    UserID,
    MemberType,
    DefaultFYear,
    StartDate,
    EndDate,
    AccountLevel,
    zeroAccounts,
    appliedCode,
    appliedName,
    fyStartDate,
  ]);

  useEffect(() => {
    document.title = "Income and Expenditure";
    if (!StartDate && DefaultFYear) {
      setStartDate(`${DefaultFYear}-04-01`);
    }
  }, [DefaultFYear, StartDate]);

  useEffect(() => {
    if (EndDate && DefaultFYear) {
      fetchIncomeExpenditure();
    }
  }, [fetchIncomeExpenditure, EndDate, DefaultFYear]);

  const handleSearch = () => {
    setAppliedCode((Account_Code || "").trim());
    setAppliedName((Account_Description || "").trim());
  };

  const printParams = new URLSearchParams({
    fyear: DefaultFYear || "",
    startDate: StartDate || fyStartDate || "",
    endDate: EndDate || "",
    accountLevel: AccountLevel || "0",
    showZeroAccounts: zeroAccounts ? "1" : "0",
    accountCode: Account_Code || "",
    accountName: Account_Description || "",
  });

  const formatAmount = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) {
      return value ?? "";
    }
    return n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const renderLedgerIncome = () => {
    if (LedgerIncome != null && LedgerIncome.length > 0) {
      return LedgerIncome.map((item, index) => (
        <tr key={index}>
          <td>{item.Account_Code}</td>
          <td>{item.Account_Description}</td>
          <td className="text-end">{formatAmount(item.Amount)}</td>
        </tr>
      ));
    }
    return (
      <tr>
        <td colSpan={3}>
          <h4
            className="mt-3 mb-0"
            style={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}
          >
            No Data Found
          </h4>
        </td>
      </tr>
    );
  };

  const renderLedgerExpenditure = () => {
    if (LedgerExpenses != null && LedgerExpenses.length > 0) {
      return LedgerExpenses.map((item, index) => (
        <tr key={index}>
          <td>{item.Account_Code}</td>
          <td>{item.Account_Description}</td>
          <td className="text-end">{formatAmount(item.Amount)}</td>
        </tr>
      ));
    }
    return (
      <tr>
        <td colSpan={3}>
          <h4
            className="mt-3 mb-0"
            style={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}
          >
            No Data Found
          </h4>
        </td>
      </tr>
    );
  };

  const surplusLabel = Number(SurplusOrDeficit) >= 0 ? "Surplus" : "Deficit";

  return (
    <>
      <AccountsMenu />
      <ToastContainer />
      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/Accounts/AccountsDashboard">Accounts</NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Income and Expenditure Statement
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Income and Expenditure</div>
                <p className="text-muted small mb-0">
                  Income (60*): period credits. Expenditure (70*): period debits.
                  Year: {DefaultFYear || "—"}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            <div className="card">
              <div className="card-body">
                <div className="row align-items-center gy-3">
                  <div className="col-md-2">
                    <label>Select Level</label>
                    <select
                      className="form-select"
                      value={AccountLevel}
                      onChange={(e) => setAccountLevel(e.target.value)}
                    >
                      <option value="0">3rd Level (detail)</option>
                      <option value="1">1st Level</option>
                      <option value="2">2nd Level</option>
                      <option value="3">3rd Level</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label>Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={StartDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={moment().format(config.date_format_input)}
                    />
                  </div>
                  <div className="col-md-2">
                    <label>End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={EndDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      max={moment().format(config.date_format_input)}
                    />
                  </div>
                  <div className="col-md-2">
                    <label>Account Code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={Account_Code}
                      onChange={(e) => setAccount_Code(e.target.value)}
                      placeholder="Starts with…"
                    />
                  </div>
                  <div className="col-md-2">
                    <label>Account Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={Account_Description}
                      onChange={(e) => setAccount_Description(e.target.value)}
                      placeholder="Contains…"
                    />
                  </div>
                  <div className="col-md-2">
                    <div className="form-check mt-md-4">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={zeroAccounts}
                        onChange={(e) => setZeroAccounts(e.target.checked)}
                        id="ieShowZero"
                      />
                      <label className="form-check-label" htmlFor="ieShowZero">
                        Show Zero Accounts
                      </label>
                    </div>
                  </div>
                  <div className="col-md-12 text-md-end">
                    <input
                      type="button"
                      value="Search"
                      className="button button-primary"
                      onClick={handleSearch}
                    />
                    <Link
                      to={`/Accounts/PrintIncomeExpenditure?${printParams.toString()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="button button-primary ms-2"
                    >
                      Print
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="card mt-4">
                  <div className="card-body responsive-table">
                    <table className="table table-theme table-hover mb-0">
                      <tbody>
                        <tr>
                          <th colSpan={3}>Income</th>
                        </tr>
                        <tr>
                          <th>Account Code</th>
                          <th>Account Name</th>
                          <th className="text-end">Amount</th>
                        </tr>
                        {loading ? (
                          <tr>
                            <td className="text-center" colSpan={3}>
                              <Spinner animation="border" role="status" />
                            </td>
                          </tr>
                        ) : (
                          <>
                            {renderLedgerIncome()}
                            <tr>
                              <th className="text-end" colSpan={2}>
                                Total
                              </th>
                              <th className="text-end">
                                {formatAmount(IncomeTotal)}
                              </th>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card mt-4">
                  <div className="card-body responsive-table">
                    <table className="table table-theme table-hover mb-0">
                      <tbody>
                        <tr>
                          <th colSpan={3}>Expenditure</th>
                        </tr>
                        <tr>
                          <th>Account Code</th>
                          <th>Account Name</th>
                          <th className="text-end">Amount</th>
                        </tr>
                        {loading ? (
                          <tr>
                            <td className="text-center" colSpan={3}>
                              <Spinner animation="border" role="status" />
                            </td>
                          </tr>
                        ) : (
                          <>
                            {renderLedgerExpenditure()}
                            <tr>
                              <th className="text-end" colSpan={2}>
                                Total
                              </th>
                              <th className="text-end">
                                {formatAmount(ExpanditureTotal)}
                              </th>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {!loading && (
              <div className="card mt-3">
                <div className="card-body py-2">
                  <div className="row">
                    <div className="col-md-12 text-end">
                      <strong>{surplusLabel}: </strong>
                      <span
                        className={
                          Number(SurplusOrDeficit) >= 0
                            ? "text-success"
                            : "text-danger"
                        }
                      >
                        {formatAmount(Math.abs(SurplusOrDeficit))}
                      </span>
                      <span className="text-muted small ms-2">
                        (Income {formatAmount(IncomeTotal)} − Expenditure{" "}
                        {formatAmount(ExpanditureTotal)})
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default IncomeExpenditure;

